/**
 * @file 피드백 게시판 상수·헬퍼
 * 상태 메타(라벨/색상)와 날짜 포맷 헬퍼를 모아둔다. 색은 ds 토큰과 동일 계열.
 */
import dayjs from "dayjs";

/**
 * 상태 메타 — 서버 enum(REVIEW|IN_PROGRESS|PARTIAL_DONE|DONE|REJECTED) 기준.
 * 승인표(STATUS_META) 패턴과 동일.
 *
 * 부분 완료는 진행중(주황)과 완료(초록) 사이의 청록으로 둔다. 초록 계열을 나눠 쓰면 칩만 보고
 * 완료와 구분되지 않고, 진행중과 같은 주황이면 "뭐라도 나왔다"는 게 안 드러난다.
 *
 * 보류/거절은 장미색. 검토중과 같은 회색으로 두면 "아직 안 봤다" 와 "안 하기로 했다" 가 칩만 보고
 * 구분되지 않는데, 작성자에게는 이 둘이 정반대다.
 */
export const FEEDBACK_STATUS_META = {
    REVIEW:       { label: "검토중",   color: "#64748b", bg: "#f1f5f9" },
    IN_PROGRESS:  { label: "진행중",   color: "#d97706", bg: "#fffbeb" },
    PARTIAL_DONE: { label: "부분 완료", color: "#0891b2", bg: "#ecfeff" },
    DONE:         { label: "완료",     color: "#16a34a", bg: "#f0fdf4" },
    REJECTED:     { label: "보류/거절", color: "#e11d48", bg: "#fff1f2" },
};

/**
 * 상태 필터/선택 옵션 순서 — 처리 흐름 순(서버 enum 선언 순서와 동일하게 유지).
 * REJECTED 는 흐름에서 빠지는 종착점이라 맨 뒤 (서버 FeedbackStatus 주석과 같은 이유).
 */
export const FEEDBACK_STATUS_ORDER = ["REVIEW", "IN_PROGRESS", "PARTIAL_DONE", "DONE", "REJECTED"];

export function statusMeta(status) {
    return FEEDBACK_STATUS_META[status] || FEEDBACK_STATUS_META.REVIEW;
}

/** 바꾸면 작성자에게 메일이 나가는 상태 — 서버 FeedbackService.notifyStatusChange 와 같은 집합. */
export const MAIL_SENDING_STATUSES = ["PARTIAL_DONE", "DONE"];

/**
 * 상태 변경 전 확인창 문구 — 확인이 필요 없으면 null(바로 변경).
 *
 * 카드(FeedbackPage)와 상세 모달이 같은 판단을 각자 하드코딩하고 있었다. 메일이 나가는 상태가
 * 늘어날 때 한쪽만 고치면 "목록에서 바꾸면 확인 없이 메일이 나가는" 형태로 갈린다 — 실제로 이
 * 화면의 안내 목록이 그렇게 어긋난 적이 있어서(config/feedbackStatus.js 주석) 판단을 여기 하나로 둔다.
 *
 * 기준은 '작성자에게 메일이 나가는가' 다. 사내 알림만 가는 상태는 되돌리기 쉬우므로 묻지 않는다.
 */
export function statusChangeConfirm(nextStatus) {
    if (!MAIL_SENDING_STATUSES.includes(nextStatus)) return null;
    const label = statusMeta(nextStatus).label;   // "완료" / "부분 완료"
    return {
        title: `${label} 처리`,
        message: `${label}로 변경하면 작성자에게 ${label} 안내 메일이 발송됩니다.\n진행할까요?`,
    };
}

/** 상대 시간 (방금 전 / n분 전 / n시간 전 / n일 전 / 날짜). */
export function formatRelativeTime(iso) {
    if (!iso) return "";
    const d = dayjs(iso);
    if (!d.isValid()) return "";
    const now = dayjs();
    const diffSec = now.diff(d, "second");
    if (diffSec < 60) return "방금 전";
    const diffMin = now.diff(d, "minute");
    if (diffMin < 60) return `${diffMin}분 전`;
    const diffHour = now.diff(d, "hour");
    if (diffHour < 24) return `${diffHour}시간 전`;
    const diffDay = now.diff(d, "day");
    if (diffDay < 7) return `${diffDay}일 전`;
    return d.format(now.year() === d.year() ? "M월 D일" : "YYYY.MM.DD");
}

/** 절대 시간 (툴팁용). */
export function formatDateTime(iso) {
    if (!iso) return "";
    const d = dayjs(iso);
    return d.isValid() ? d.format("YYYY.MM.DD HH:mm") : "";
}
